import { memo, useMemo, type FC } from "react";
import { useQuery } from "@apollo/client";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FINANCING_TYPES_QUERY } from "@/apis/taxonomy/queries";
import { HasFinancingEventInclusionMethod } from "@/generated/company-search";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { useCompanyFilterActions, useCompanyFilterSlice } from "../../hooks";
import { DISCOVERED_WITHIN_OPTIONS, INCLUSION_LABELS } from "./options";
import {
  defaultFinancingEventsSlice,
  type FinancingEventsSlice,
} from "./slice";

type FinancingTypesPayload = {
  financingTypes: { data: { id: string; name: string }[] };
};

const OFF = "off";
const ANY_TIME = "any";

export const FinancingEventsFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice(
    "financingEvents"
  ) as FinancingEventsSlice;
  const { setSlice } = useCompanyFilterActions();
  const update = (next: Partial<FinancingEventsSlice>) =>
    setSlice("financingEvents", (prev) => ({ ...prev, ...next }));

  const { data, loading } = useQuery<FinancingTypesPayload>(
    FINANCING_TYPES_QUERY,
    { context: { clientName: TAXONOMY_CLIENT } }
  );
  const financingTypes = useMemo(
    () => (data?.financingTypes?.data ?? []).map((type) => type.name),
    [data]
  );

  const disabled = !slice.inclusionMethod;

  return (
    <Stack spacing={2} data-testid="financing-events-filter">
      <Typography variant="body2" color="text.secondary">
        Only show companies with (or without) a matching financing event. It
        narrows the companies in every export, not only the Financing Events
        file.
      </Typography>

      <TextField
        select
        size="small"
        label="Companies"
        value={slice.inclusionMethod ?? OFF}
        onChange={(event) =>
          event.target.value === OFF
            ? setSlice("financingEvents", defaultFinancingEventsSlice)
            : update({
                inclusionMethod: event.target
                  .value as HasFinancingEventInclusionMethod,
              })
        }
        inputProps={{ "data-testid": "financing-events-inclusion-select" }}
      >
        <MenuItem value={OFF}>All companies</MenuItem>
        {Object.values(HasFinancingEventInclusionMethod).map((method) => (
          <MenuItem key={method} value={method}>
            {INCLUSION_LABELS[method]}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Discovered"
        disabled={disabled}
        value={slice.discoveredWithinDays ?? ANY_TIME}
        onChange={(event) =>
          update({
            discoveredWithinDays:
              event.target.value === ANY_TIME
                ? null
                : Number(event.target.value),
          })
        }
        helperText="Rolling window: scheduled deliveries move it forward each run."
        inputProps={{ "data-testid": "financing-events-discovered-select" }}
      >
        {DISCOVERED_WITHIN_OPTIONS.map(({ value, label }) => (
          <MenuItem key={label} value={value ?? ANY_TIME}>
            {label}
          </MenuItem>
        ))}
      </TextField>

      <Autocomplete
        multiple
        size="small"
        disabled={disabled}
        loading={loading}
        options={financingTypes}
        value={slice.financingTypes}
        onChange={(_event, financingTypes) => update({ financingTypes })}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Financing type"
            placeholder={slice.financingTypes.length ? "" : "Any round"}
            inputProps={{
              ...params.inputProps,
              "data-testid": "financing-events-type-input",
            }}
          />
        )}
      />

      <TextField
        type="number"
        size="small"
        label="Minimum amount ($)"
        disabled={disabled}
        value={slice.minAmount ?? ""}
        onChange={(event) =>
          update({
            minAmount:
              event.target.value === "" ? null : Number(event.target.value),
          })
        }
        helperText="Events without a reported amount are not matched when set."
        inputProps={{
          min: 0,
          "data-testid": "financing-events-min-amount-input",
        }}
      />
    </Stack>
  );
});

FinancingEventsFilter.displayName = "FinancingEventsFilter";
